import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { azureCloudTypeMetadata } from '../consts/metadata-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { ConnectionAuthKind, CredentialTypeOptions, oDataAuthTypes } from 'shell-models-lib';
import { mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';

const authOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_Select_Basic_Option,
        value: oDataAuthTypes.BasicAuthentication
    },
    {
        label: stringResources.AuthenticationType_Select_Windows_Option,
        value: oDataAuthTypes.WindowsAuthentication
    },
    {
        label: stringResources.Anonymous_DisplayText,
        value: oDataAuthTypes.AnonymousAuthentication
    },
    {
        label: stringResources.AuthenticationType_Select_ServicePrincipalKey_Option,
        value: oDataAuthTypes.AADServicePrincipalWithKey
    },
    {
        label: stringResources.AuthenticationType_Select_ServicePrincipalCert_Option,
        value: oDataAuthTypes.AADServicePrincipalWithCert
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: oDataAuthTypes.MSIAuthentication,
        hidden: !GlobalVariables.enableODataSAMI
    }
];

const dynamicControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'url',
            label: stringResources.ModelName_ServiceUrl,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        options: mergeUserAuthCredentialOption(authOptions, oDataAuthTypes.UserAuth),
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: oDataAuthTypes.BasicAuthentication,
        cases: [
            {
                caseValue: oDataAuthTypes.BasicAuthentication,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true
                            }
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
                            stringResources.ModelName_Password)
                    }
                ]
            },
            {
                caseValue: oDataAuthTypes.WindowsAuthentication,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true
                            }
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
                            stringResources.ModelName_Password)
                    }
                ]
            },
            {
                caseValue: oDataAuthTypes.MSIAuthentication,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'aadResourceId',
                            label: stringResources.ModelName_AADResourceId,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'management-identity-info',
                        resourceType: stringResources.OData_DisplayText,
                        link: 'https://learn.microsoft.com/azure/data-factory/connector-odata?tabs=data-factory#linked-service-properties'
                    }
                ]
            },
            {
                caseValue: oDataAuthTypes.AADServicePrincipalWithKey,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'aadResourceId',
                            label: stringResources.ModelName_AADResourceId,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'tenant-textbox',
                            bind: 'tenant',
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true,
                            label: stringResources.ModelName_Tenant,
                            required: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'servicePrincipalId',
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            label: stringResources.ModelName_ServicePrincipalID,
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'servicePrincipalKey',
                        label: stringResources.ModelName_ServicePrincipalKey,
                        child: {
                            type: 'parameterizable',
                            attribute: 'servicePrincipalKey',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_ServicePrincipalKey,
                                required: true,
                                isLabelVertical: true
                            }
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
                            stringResources.ModelName_ServicePrincipalKey)
                    },
                    azureCloudTypeMetadata
                ]
            },
            {
                caseValue: oDataAuthTypes.AADServicePrincipalWithCert,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'aadResourceId',
                            label: stringResources.ModelName_AADResourceId,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'tenant-textbox',
                            bind: 'tenant',
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true,
                            label: stringResources.ModelName_Tenant,
                            required: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'servicePrincipalId',
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            label: stringResources.ModelName_ServicePrincipalID,
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'servicePrincipalEmbeddedCert',
                        label: stringResources.ModelName_ServicePrincipalEmbeddedCert,
                        child: {
                            type: 'parameterizable',
                            attribute: 'servicePrincipalEmbeddedCert',
                            disableAddParameter: true,
                            child: {
                                type: 'key-file-content',
                                label: stringResources.ModelName_ServicePrincipalEmbeddedCert,
                                required: true,
                                isLabelVertical: true
                            }
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
                            stringResources.ModelName_ServicePrincipalEmbeddedCert)
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'servicePrincipalEmbeddedCertPassword',
                        label: stringResources.ModelName_ServicePrincipalEmbeddedCertPassword,
                        child: {
                            type: 'parameterizable',
                            attribute: 'servicePrincipalEmbeddedCertPassword',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_ServicePrincipalEmbeddedCertPassword,
                                isLabelVertical: true
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
                            stringResources.ModelName_ServicePrincipalEmbeddedCertPassword)
                    },
                    azureCloudTypeMetadata
                ]
            },
            {
                caseValue: oDataAuthTypes.UserAuth,
                children: [
                    {
                        type: 'credential-select',
                        hide: !GlobalVariables.enableConnectorUserAuthCred,
                        bind: 'credential',
                        filterCredentialType: CredentialTypeOptions.UserAuth,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        type: 'key-val-list',
        bind: 'authHeaders',
        label: stringResources.ModelName_AuthHeaders,
        description: stringResources.AuthHeaders_Description,
        keyOptions: [
            {
                label: 'Authorization', // display the actual object key, do not need localization
                value: 'Authorization'
            },
            {
                label: 'APIKey',
                value: 'APIKey'
            }
        ],
        keyType: 'dropdown',
        dropDownEditable: true,
        isValueSecret: true
    }
];

export const oDataMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.odata.linkedServiceType,
        displayName: stringResources.OData_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-OData'
        },
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.Odata
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Anonymous, ConnectionAuthKind.Basic],
        supportCopyJobSource: true,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        tableNameAlias: 'path',
        isFirstClass: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.odata.copySourceType],
        supportDatasetV2: true,
        supportOPDG: GlobalVariables.enableOPDG,
        category: ['GenericProtocol'],
        onetimeQueryTemplate: '',
        // eslint-disable-next-line max-len
        timedQueryTemplate: '$filter=<<columnName>> ge datetime\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-ddTHH:mm:ssZ\' )}\' \nand <<columnName>> lt datetime\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-ddTHH:mm:ssZ\' )}\'',
        deltaLoadQueryTemplate: `$filter=<<columnName>> ge @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('datetime''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-ddTHH:mm:ssZ'), ''''))}
        and <<columnName>> lt @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('datetime''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-ddTHH:mm:ssZ'), ''''))}`,
        supportFirstClassParameterExperience: true,
        controls: dynamicControls
    };

    return metadata;
};
